/**
 * hexToOklch — Convert a hex color string to oklch components.
 *
 * Uses the sRGB → Linear RGB → OKLab → OKLCH pipeline.
 * This is needed to derive --primary-h and --primary-c CSS custom properties
 * from user-selected hex accent colors.
 *
 * @see https://bottosson.github.io/posts/oklab/
 */

/** Parse hex string (#RRGGBB or #RGB) to [r, g, b] in 0–1 range */
function hexToSrgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "")
  const full =
    h.length === 3
      ? (h[0] ?? "") +
        (h[0] ?? "") +
        (h[1] ?? "") +
        (h[1] ?? "") +
        (h[2] ?? "") +
        (h[2] ?? "")
      : h

  const r = parseInt(full.slice(0, 2), 16) / 255
  const g = parseInt(full.slice(2, 4), 16) / 255
  const b = parseInt(full.slice(4, 6), 16) / 255
  return [r, g, b]
}

/** sRGB gamma → linear RGB */
function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** Linear RGB → OKLab (Björn Ottosson's method) */
function linearRgbToOklab(
  r: number,
  g: number,
  b: number,
): [number, number, number] {
  const l_ = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b
  const m_ = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b
  const s_ = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b

  const l = Math.cbrt(l_)
  const m = Math.cbrt(m_)
  const s = Math.cbrt(s_)

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const bLab = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

  return [L, a, bLab]
}

export interface OklchComponents {
  /** Lightness 0–1 */
  l: number
  /** Chroma 0+ */
  c: number
  /** Hue 0–360 */
  h: number
}

/**
 * Convert a hex color (#RRGGBB or #RGB) to oklch components.
 *
 * Returns { l, c, h } where:
 *   - l = lightness (0–1)
 *   - c = chroma (0–0.37 typically)
 *   - h = hue (0–360 degrees)
 */
export function hexToOklch(hex: string): OklchComponents {
  const [sr, sg, sb] = hexToSrgb(hex)
  const lr = srgbToLinear(sr)
  const lg = srgbToLinear(sg)
  const lb = srgbToLinear(sb)

  const [L, a, b] = linearRgbToOklab(lr, lg, lb)

  const c = Math.sqrt(a * a + b * b)
  let h = (Math.atan2(b, a) * 180) / Math.PI
  if (h < 0) h += 360

  return {
    l: Math.round(L * 10000) / 10000,
    c: Math.round(c * 10000) / 10000,
    h: Math.round(h * 100) / 100,
  }
}
